import { formatCurrencyAmount } from '@/utils/currency.utils';

export const DATE_KEYS = [
  'nextBillingDate',
  'next_billing_date',
  'nextBillingAt',
  'next_billing_at',
  'nextPaymentDate',
  'next_payment_date',
  'nextPaymentAt',
  'next_payment_at',
  'currentPeriodEnd',
  'current_period_end',
  'renewalDate',
  'renewal_date',
  'renewsAt',
  'renews_at',
  'expiresAt',
  'expires_at',
  'trialEndsAt',
  'trial_ends_at',
  'nextChargeDate',
  'next_charge_date',
  'billingDate',
  'billing_date',
];

export const AMOUNT_KEYS = [
  'nextBillingAmount',
  'next_billing_amount',
  'nextPaymentAmount',
  'next_payment_amount',
  'amount',
  'price',
  'planAmount',
  'plan_amount',
  'subscription_amount',
  'billing_amount',
  'monthly_price',
  'rate',
];

export const CURRENCY_KEYS = [
  'currencySymbol',
  'currency_symbol',
  'currency',
  'currencyCode',
  'currency_code',
];

export const CANCEL_DATE_KEYS = [
  'cancelAt',
  'cancel_at',
  'cancelAtPeriodEnd',
  'cancel_at_period_end',
  'cancelAtPeriodEndDate',
  'cancel_at_period_end_date',
  'cancelAtPeriodEndAt',
  'cancel_at_period_end_at',
  'canceledAt',
  'canceled_at',
  'cancelledAt',
  'cancelled_at',
  'endsAt',
  'ends_at',
  'cancellationDate',
  'cancellation_date',
  'cancelAtTime',
  'cancelTime',
];

export const DEFAULT_LIFETIME_CUTOFF = '2025-11-10T00:00:00.000Z';
export const DEFAULT_GALLERY_PLAN = 'standard';
export const CHANNEL_CHECKOUT_SUCCESS_FLAG = 'channel_paid';
export const CHANNEL_CHECKOUT_CHANNEL_ID_PARAM = 'channel_id';
export const CHANNEL_CHECKOUT_POLL_INTERVAL_MS = 1000;
export const CHANNEL_CHECKOUT_POLL_MAX_ATTEMPTS = 45;
export function coerceDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value);
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed);
    }
  }

  if (typeof value === 'object') {
    if (typeof value.seconds === 'number' && Number.isFinite(value.seconds)) {
      return new Date(value.seconds * 1000);
    }
    if (typeof value.millis === 'number' && Number.isFinite(value.millis)) {
      return new Date(value.millis);
    }
    if (typeof value.value === 'string') {
      const parsed = Date.parse(value.value);
      if (!Number.isNaN(parsed)) {
        return new Date(parsed);
      }
    }
    if (typeof value.date === 'string') {
      const parsed = Date.parse(value.date);
      if (!Number.isNaN(parsed)) {
        return new Date(parsed);
      }
    }
  }

  return null;
}
export function resolveNextBillingDate(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }

  for (const key of DATE_KEYS) {
    const candidate = coerceDate(subscription[key]);
    if (candidate) {
      return candidate;
    }
  }

  const metadata = subscription.metadata ?? subscription.meta ?? null;
  if (metadata && typeof metadata === 'object') {
    for (const key of DATE_KEYS) {
      const candidate = coerceDate(metadata[key]);
      if (candidate) {
        return candidate;
      }
    }
  }

  return null;
}

export function resolveAmountValue(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }

  for (const key of AMOUNT_KEYS) {
    const candidate = subscription[key];
    if (candidate == null) {
      continue;
    }
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const parsed = Number(candidate.replace(/[^0-9.-]+/g, ''));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  const metadata = subscription.metadata ?? subscription.meta ?? null;
  if (metadata && typeof metadata === 'object') {
    for (const key of AMOUNT_KEYS) {
      const candidate = metadata[key];
      if (candidate == null) {
        continue;
      }
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
      if (typeof candidate === 'string') {
        const parsed = Number(candidate.replace(/[^0-9.-]+/g, ''));
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
  }

  const priceObject = subscription.price ?? subscription?.price_details ?? null;
  if (priceObject && typeof priceObject === 'object') {
    const valueCandidates = [
      priceObject.totalWithVat,
      priceObject.total_with_vat,
      priceObject.total,
      priceObject.amount,
      priceObject.price,
      priceObject.value,
      priceObject.rate,
    ];
    for (const candidate of valueCandidates) {
      if (candidate == null) {
        continue;
      }
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
      if (typeof candidate === 'string') {
        const parsed = Number(candidate.replace(/[^0-9.-]+/g, ''));
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
  }

  return null;
}

export function resolvePriceAmount(price) {
  if (!price || typeof price !== 'object') {
    return null;
  }
  const candidates = [
    price.totalWithVat,
    price.total_with_vat,
    price.total,
    price.amount,
    price.price,
    price.value,
  ];
  for (const candidate of candidates) {
    if (candidate == null) {
      continue;
    }
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const parsed = Number(candidate.replace(/[^0-9.-]+/g, ''));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

export function resolveCurrency(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return 'EUR';
  }

  for (const key of CURRENCY_KEYS) {
    const candidate = subscription[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  const metadata = subscription.metadata ?? subscription.meta ?? null;
  if (metadata && typeof metadata === 'object') {
    for (const key of CURRENCY_KEYS) {
      const candidate = metadata[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  const priceObject = subscription.price ?? subscription?.price_details ?? null;
  if (priceObject && typeof priceObject === 'object') {
    const candidate = priceObject.currency ?? priceObject.currencyCode ?? priceObject.currency_code ?? priceObject.country ?? null;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  if (typeof subscription.currency_symbol === 'string' && subscription.currency_symbol.trim()) {
    return subscription.currency_symbol.trim();
  }

  return 'EUR';
}

export function formatGalleryPriceHint(price) {
  if (!price || typeof price !== 'object') {
    return null;
  }
  const amount = Number(price.amount ?? price.price ?? price.value ?? NaN);
  const totalWithVat = Number(
    price.totalWithVat ?? price.total_with_vat ?? price.total ?? NaN
  );
  const currency = price.currency ?? price.currencyCode ?? price.currency_code ?? price.country ?? null;
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  const formattedAmount = formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (Number.isFinite(totalWithVat) && totalWithVat > amount) {
    const formattedTotalWithVat = formatCurrencyAmount(totalWithVat, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!formattedAmount || !formattedTotalWithVat) {
      return null;
    }
    return `${formattedAmount} + VAT -> ${formattedTotalWithVat}/month - cancel anytime`;
  }
  if (!formattedAmount) {
    return null;
  }
  return `${formattedAmount}/month - cancel anytime`;
}

export function formatStatus(subscription, isActive) {
  const raw = subscription?.status ?? subscription?.state ?? null;
  if (typeof raw === 'string' && raw.trim()) {
    const text = raw
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (match) => match.toUpperCase());
    return text;
  }

  if (isActive) {
    return 'Active';
  }

  return 'Inactive';
}

export function resolveSubscriptionIsActive(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return false;
  }

  if (typeof subscription.isActive === 'boolean') {
    return subscription.isActive;
  }

  if (typeof subscription.is_active === 'boolean') {
    return subscription.is_active;
  }

  const rawStatus = subscription.status ?? subscription.state ?? null;
  const normalizedStatus = typeof rawStatus === 'string'
    ? rawStatus.trim().toLowerCase()
    : '';
  const hasActiveStatus = [
    'active',
    'trialing',
    'paid',
    'confirmed',
    'active_subscription',
  ].includes(normalizedStatus);

  if (!hasActiveStatus) {
    return false;
  }

  const cancelAtDate = resolveCancelAtDate(subscription);
  if (!(cancelAtDate instanceof Date) || !Number.isFinite(cancelAtDate.getTime())) {
    return true;
  }

  return cancelAtDate.getTime() > Date.now();
}

export function formatNextBillingLabel(date) {
  if (!date) {
    return 'Billing date not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function resolveCancelAtDate(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }

  for (const key of CANCEL_DATE_KEYS) {
    const candidate = coerceDate(subscription[key]);
    if (candidate) {
      return candidate;
    }
  }

  const metadata = subscription.metadata ?? subscription.meta ?? null;
  if (metadata && typeof metadata === 'object') {
    for (const key of CANCEL_DATE_KEYS) {
      const candidate = coerceDate(metadata[key]);
      if (candidate) {
        return candidate;
      }
    }
  }

  return null;
}
